import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Megaphone, Send, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BIZ_CATEGORIES = [
  "Acting Institute", "Dance Academy", "Singing Institute", "Modeling Institute",
  "Grooming Institute", "Camera Rental", "Recording Studio", "Fashion Brand",
  "Production House", "Photographer", "Makeup Artist", "Acting Coach",
  "Voice Coach", "Other Entertainment Business",
];
const PLACEMENTS = [
  { k: "home", label: "Home banner" },
  { k: "top_banner", label: "Top banner" },
  { k: "mid_feed", label: "Mid-feed (every 8-10 posts)" },
  { k: "bottom_banner", label: "Bottom banner" },
  { k: "between_categories", label: "Between categories" },
  { k: "between_search", label: "Between search results" },
  { k: "search", label: "Search page" },
  { k: "profile", label: "Profile page" },
  { k: "performance_detail", label: "Performance detail" },
  { k: "full_width", label: "Full-width promotional" },
];
const DURATIONS = ["7 days", "15 days", "30 days", "60 days", "90 days"];

export default function AdvertiseModal({ open, onClose }) {
  const [form, setForm] = useState({
    business_name: "", category: BIZ_CATEGORIES[0], contact_person: "", phone: "",
    email: "", city: "", website: "", whatsapp: "",
    goal: "", preferred_placement: "home", duration: "30 days", message: "",
  });
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.contact_person.trim() || !form.phone.trim()) {
      return toast.error("Business name, contact person aur phone required hain");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (banner) fd.append("banner", banner);
      await api.post("/advertise/request", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Request received — our team will call you within 24 hrs");
      setForm({ business_name: "", category: BIZ_CATEGORIES[0], contact_person: "", phone: "", email: "", city: "", website: "", whatsapp: "", goal: "", preferred_placement: "home", duration: "30 days", message: "" });
      setBanner(null);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="advertise-modal" className="bg-[#0A0605] border-zinc-800 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Megaphone size={16} className="text-[#F5C462]"/> Advertise with NextScene</DialogTitle>
          <p className="text-xs text-zinc-400 mt-1">Fill karein — hum 24 hrs me contact karenge. Payment manually process hota hai V1 me.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-zinc-300 text-xs">Business name *</Label><Input data-testid="adv-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" required/></div>
            <div>
              <Label className="text-zinc-300 text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="adv-category" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
                  {BIZ_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-zinc-300 text-xs">Contact person *</Label><Input data-testid="adv-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800" required/></div>
            <div><Label className="text-zinc-300 text-xs">Phone *</Label><Input data-testid="adv-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="mt-1 bg-zinc-900 border-zinc-800" required/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-zinc-300 text-xs">Email</Label><Input data-testid="adv-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/></div>
            <div><Label className="text-zinc-300 text-xs">City</Label><Input data-testid="adv-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-zinc-300 text-xs">Website</Label><Input data-testid="adv-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className="mt-1 bg-zinc-900 border-zinc-800"/></div>
            <div><Label className="text-zinc-300 text-xs">WhatsApp</Label><Input data-testid="adv-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/></div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Advertisement goal</Label>
            <Textarea data-testid="adv-goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="What do you want to achieve? (leads, calls, admissions…)" className="mt-1 bg-zinc-900 border-zinc-800 min-h-[60px]" maxLength={400}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-300 text-xs">Preferred placement</Label>
              <Select value={form.preferred_placement} onValueChange={(v) => setForm({ ...form, preferred_placement: v })}>
                <SelectTrigger data-testid="adv-placement" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
                  {PLACEMENTS.map(p => <SelectItem key={p.k} value={p.k}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Duration</Label>
              <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                <SelectTrigger data-testid="adv-duration" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
                  {DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label data-testid="adv-banner-label" className="rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#F5C462]/50 h-20 flex flex-col items-center justify-center text-xs text-zinc-300 cursor-pointer transition-colors">
            <ImageIcon size={16} className={`mb-1 ${banner ? "text-emerald-400" : "text-zinc-500"}`}/>
            <span>{banner ? banner.name.slice(0, 30) : "Upload banner image (≤5MB, optional)"}</span>
            <input data-testid="adv-banner" type="file" accept="image/*" className="hidden" onChange={(e) => setBanner(e.target.files?.[0] || null)}/>
          </label>
          <div>
            <Label className="text-zinc-300 text-xs">Additional notes</Label>
            <Textarea data-testid="adv-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Budget, timing, etc." className="mt-1 bg-zinc-900 border-zinc-800 min-h-[60px]" maxLength={800}/>
          </div>
          <DialogFooter>
            <Button type="button" onClick={onClose} className="rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm">Cancel</Button>
            <Button data-testid="adv-submit" type="submit" disabled={busy} className="rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
              {busy ? <Loader2 className="animate-spin mr-2" size={14}/> : <Send size={14} className="mr-1"/>} Send request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
